<h2 mat-dialog-title i18n>Chart designer — {{ data.tableName }}</h2>

<mat-dialog-content class="dialog-content">
  <div class="designer-layout" [formGroup]="form">
    <!-- ============ LEFT: configuration ============ -->
    <div class="config-col">
      <section class="block">
        <h3 i18n>1. Chart type</h3>
        <div class="chart-type-grid">
          @for (ct of chartTypes; track ct.id) {
            <button
              type="button"
              class="chart-type-opt"
              [class.active]="chartType === ct.id"
              (click)="onChartTypeChange(ct.id)"
            >
              <span class="ct-label">{{ ct.label }}</span>
              <span class="ct-sub">{{ ct.sub }}</span>
            </button>
          }
        </div>
      </section>

      <section class="block">
        <h3 i18n>2. Data source columns</h3>
        <p class="hint" i18n>
          From table <b>{{ data.tableName }}</b>. Only columns valid for each role
          are offered below.
        </p>
        <div class="col-ref-list">
          @for (c of columns; track c.columnKey) {
            <div class="col-ref">
              <span
                ><b>{{ c.name || c.columnKey }}</b> ·
                <code>{{ c.columnKey }}</code>
                @if (columnAxisNote(c)) {
                  <em class="axis-note">— {{ columnAxisNote(c) }}</em>
                }
              </span>
              <span class="type">{{ columnTypeLabel(c.type) }}</span>
            </div>
          }
        </div>
      </section>

      <section class="block">
        <h3 i18n>3. Axes &amp; series</h3>

        <mat-form-field appearance="outline" class="full">
          <mat-label>{{
            needsYAxis ? "X-axis column" : "Category / X-axis column"
          }}</mat-label>
          <mat-select formControlName="categoryColumnKey">
            <mat-option [value]="null" i18n>— select —</mat-option>
            @for (c of categoryColumns(); track c.columnKey) {
              <mat-option [value]="c.columnKey">{{
                c.name || c.columnKey
              }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (needsYAxis) {
          <mat-form-field appearance="outline" class="full">
            <mat-label i18n>Y-axis column</mat-label>
            <mat-select formControlName="yAxisColumnKey">
              <mat-option [value]="null" i18n>— select —</mat-option>
              @for (c of yAxisColumns(); track c.columnKey) {
                <mat-option [value]="c.columnKey">{{
                  c.name || c.columnKey
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        @if (!needsBands) {
          <mat-form-field appearance="outline" class="full">
            <mat-label i18n>Aggregation (when a category repeats)</mat-label>
            <mat-select formControlName="aggregation">
              @for (agg of aggregations; track agg) {
                <mat-option [value]="agg">{{ agg }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <div class="subhead">
          <span i18n>Value series</span>
          @if (meta.maxSeries > 1) {
            <button
              mat-stroked-button
              type="button"
              (click)="addSeries()"
              [disabled]="series.length >= meta.maxSeries"
            >
              <mat-icon>add</mat-icon><span i18n>Add series</span>
            </button>
          }
        </div>

        <div formArrayName="series">
          @for (s of series.controls; track s; let i = $index) {
            <div class="series-row" [formGroupName]="i">
              <mat-form-field appearance="outline" class="grow">
                <mat-label i18n>Value column</mat-label>
                <mat-select formControlName="valueColumnKey">
                  <mat-option [value]="null" i18n>— value column —</mat-option>
                  @for (c of seriesColumns(i); track c.columnKey) {
                    <mat-option [value]="c.columnKey">{{
                      c.name || c.columnKey
                    }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="grow">
                <mat-label i18n>Legend label</mat-label>
                <input matInput formControlName="label" />
              </mat-form-field>
              @if (!needsBands) {
                <input
                  type="color"
                  class="color-input"
                  formControlName="color"
                  aria-label="Series color"
                  i18n-aria-label
                />
              }
              @if (series.length > 1) {
                <button
                  mat-icon-button
                  type="button"
                  color="warn"
                  (click)="removeSeries(i)"
                  aria-label="Remove series"
                  i18n-aria-label
                >
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </div>
          }
        </div>
      </section>

      @if (needsBands) {
        <section class="block">
          <h3 i18n>4. Value bands</h3>
          <p class="hint" i18n>
            Bands are contiguous — each band starts where the one below it ends.
            Raising an edge pushes every edge above it up with it.
          </p>

          <div class="band-bar">
            @for (bc of bandColors.controls; track bc; let i = $index) {
              <div
                class="band-seg"
                [style.flexGrow]="bandWeight(i)"
                [style.background]="bandColors.at(i).value"
                [attr.title]="bandRangeText(i)"
              ></div>
            }
          </div>

          <div class="band-row band-head">
            <span class="band-from" i18n>From</span>
            <span class="band-to-h" i18n>To</span>
            <span class="band-col-h" i18n>Colour</span>
            <span></span>
          </div>

          <div class="band-row">
            <span class="band-from" i18n>Range start</span>
            <input
              type="number"
              class="band-edge"
              [step]="bandStep"
              [value]="edgeValue(0)"
              (change)="setBandEdge(0, $any($event.target).value)"
              aria-label="Range start"
              i18n-aria-label
            />
            <span></span>
            <span></span>
          </div>

          <div class="band-list">
            @for (bc of bandColors.controls; track bc; let i = $index) {
              <div class="band-row">
                <span class="band-from">
                  <b i18n>Band {{ i + 1 }}</b>
                  {{ fmt(edgeValue(i)) }} →
                </span>
                <input
                  type="number"
                  class="band-edge"
                  [step]="bandStep"
                  [value]="edgeValue(i + 1)"
                  (change)="setBandEdge(i + 1, $any($event.target).value)"
                  aria-label="Band upper edge"
                  i18n-aria-label
                />
                <input
                  type="color"
                  class="color-input"
                  [value]="bandColors.at(i).value"
                  (input)="setBandColor(i, $any($event.target).value)"
                  aria-label="Band colour"
                  i18n-aria-label
                />
                @if (bandColors.length > 1) {
                  <button
                    mat-icon-button
                    type="button"
                    color="warn"
                    (click)="removeBand(i)"
                    aria-label="Remove band"
                    i18n-aria-label
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </div>
            }
          </div>
          <button mat-stroked-button type="button" (click)="addBand()">
            <mat-icon>add</mat-icon><span i18n>Add band</span>
          </button>
        </section>
      }

      <section class="block">
        <h3 i18n>5. Labels &amp; display</h3>
        <mat-form-field appearance="outline" class="full">
          <mat-label i18n>Name (admin-facing)</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label i18n>Pdf tag</mat-label>
          <input matInput formControlName="renderTag" />
          <mat-hint i18n>Placeholder token used in the docx template</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label i18n>Chart title</mat-label>
          <input matInput formControlName="title" />
        </mat-form-field>
        <div class="two-col">
          <mat-form-field appearance="outline">
            <mat-label i18n>X-axis label</mat-label>
            <input matInput formControlName="xAxisLabel" />
          </mat-form-field>
          @if (needsYAxis) {
            <mat-form-field appearance="outline">
              <mat-label i18n>Y-axis label</mat-label>
              <input matInput formControlName="yAxisLabel" />
            </mat-form-field>
          }
        </div>
        @if (needsYAxis) {
          <mat-form-field appearance="outline" class="full">
            <mat-label i18n>Z-axis / value label</mat-label>
            <input matInput formControlName="zAxisLabel" />
          </mat-form-field>
        }
        <div class="two-col">
          <mat-form-field appearance="outline">
            <mat-label i18n>Width (px)</mat-label>
            <input
              type="number"
              matInput
              formControlName="widthPx"
              min="300"
              max="1000"
              step="10"
            />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label i18n>Height (px)</mat-label>
            <input
              type="number"
              matInput
              formControlName="heightPx"
              min="220"
              max="800"
              step="10"
            />
          </mat-form-field>
        </div>
        <mat-form-field appearance="outline" class="full">
          <mat-label i18n="@@position">Position</mat-label>
          <input type="number" matInput formControlName="sort" />
        </mat-form-field>
        <div class="checks">
          <mat-checkbox formControlName="showLegend" i18n>Show legend</mat-checkbox>
          <mat-checkbox formControlName="showDataLabels" i18n
            >Show data labels</mat-checkbox
          >
          <mat-checkbox formControlName="attachDataTable" i18n
            >Attach data table</mat-checkbox
          >
          <span class="check-hint" i18n
            >Renders the source data table beneath the chart in the generated
            document.</span
          >
        </div>
      </section>
    </div>

    <!-- ============ RIGHT: validation + preview ============ -->
    <div class="preview-col">
      @if (errors.length === 0) {
        <div class="validation ok" i18n>Valid definition — ready to save.</div>
      } @else {
        <div class="validation err">
          <b i18n
            >{errors.length, plural, one {# issue to fix} other {# issues to fix}}</b
          >
          <ul>
            @for (e of errors; track e) {
              <li>{{ e }}</li>
            }
          </ul>
        </div>
      }

      <div class="gen-row">
        <mat-form-field appearance="outline" class="rows-field">
          <mat-label>{{
            needsYAxis ? "Grid points / axis" : "Rows"
          }}</mat-label>
          <input
            type="number"
            matInput
            [(ngModel)]="rowCount"
            [ngModelOptions]="{ standalone: true }"
            [min]="3"
            [max]="needsYAxis ? 10 : 30"
            (change)="onRangeChange()"
          />
        </mat-form-field>
        <mat-form-field appearance="outline" class="rows-field">
          <mat-label i18n>Value min</mat-label>
          <input
            type="number"
            matInput
            step="0.01"
            [(ngModel)]="valueMin"
            [ngModelOptions]="{ standalone: true }"
            (change)="onRangeChange()"
          />
        </mat-form-field>
        <mat-form-field appearance="outline" class="rows-field">
          <mat-label i18n>Value max</mat-label>
          <input
            type="number"
            matInput
            step="0.01"
            [(ngModel)]="valueMax"
            [ngModelOptions]="{ standalone: true }"
            (change)="onRangeChange()"
          />
        </mat-form-field>
        @if (needsBands) {
          <button
            mat-stroked-button
            type="button"
            (click)="matchValueRangeToBands()"
            i18n
          >
            = band range
          </button>
        }
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="generatePreview()"
          [disabled]="errors.length > 0"
          i18n
        >
          Generate preview data
        </button>
      </div>

      @if (chartType === "SURFACE_3D" || chartType === "AREA_BANDED_2D") {
        <div class="view-controls">
          @if (chartType === "SURFACE_3D") {
            <label>
              <span i18n>Rotation</span>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                [(ngModel)]="viewAzimuth"
                [ngModelOptions]="{ standalone: true }"
                (input)="redraw()"
              />
              <em>{{ viewAzimuth }}°</em>
            </label>
            <label>
              <span i18n>Tilt</span>
              <input
                type="range"
                min="5"
                max="85"
                step="1"
                [(ngModel)]="viewElevation"
                [ngModelOptions]="{ standalone: true }"
                (input)="redraw()"
              />
              <em>{{ viewElevation }}°</em>
            </label>
          }
          <label>
            <span i18n>Opacity</span>
            <input
              type="range"
              min="0.15"
              max="1"
              step="0.05"
              [(ngModel)]="surfaceOpacity"
              [ngModelOptions]="{ standalone: true }"
              (input)="redraw()"
            />
            <em>{{ surfaceOpacity }}</em>
          </label>
          <label class="check">
            <input
              type="checkbox"
              [(ngModel)]="smoothSurface"
              [ngModelOptions]="{ standalone: true }"
              (change)="redraw()"
            />
            <span i18n>Smooth surface</span>
          </label>
        </div>
      }

      <div class="chart-box">
        @if (previewSvg) {
          <div [innerHTML]="previewSvg"></div>
        } @else {
          <div class="chart-empty" i18n>
            Resolve validation issues, then generate preview data to see this
            configuration rendered.
          </div>
        }
      </div>

      @if (previewData) {
        <div class="data-table-wrap">
          <p class="hint" i18n>
            Edit any value below — the chart redraws live. Use this to check the
            bands / colours behave as expected.
          </p>
          <table class="data-table">
            @if (previewRows; as rows) {
              <thead>
                <tr>
                  <th>{{ rows.catCol.name || rows.catCol.columnKey }}</th>
                  @for (sc of rows.seriesCols; track sc.valueColumnKey) {
                    <th>{{ sc.label || sc.col.name || sc.valueColumnKey }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of rows.rows; track $index; let ri = $index) {
                  <tr>
                    <td>
                      <input
                        class="cell-input"
                        [value]="fmt(row.category)"
                        (change)="setRowCategory(ri, $any($event.target).value)"
                      />
                    </td>
                    @for (sc of rows.seriesCols; track sc.valueColumnKey) {
                      <td>
                        <input
                          class="cell-input num"
                          type="number"
                          step="0.01"
                          [value]="rowCell(row, sc.valueColumnKey)"
                          (change)="
                            setRowValue(
                              ri,
                              sc.valueColumnKey,
                              $any($event.target).value
                            )
                          "
                        />
                      </td>
                    }
                  </tr>
                }
              </tbody>
            }
            @if (previewGrid; as grid) {
              <thead>
                <tr>
                  <th>{{ grid.xCol.name || grid.xCol.columnKey }}</th>
                  <th>{{ grid.yCol.name || grid.yCol.columnKey }}</th>
                  <th>{{ grid.zCol.name || grid.zCol.columnKey }}</th>
                </tr>
              </thead>
              <tbody>
                @for (p of grid.points; track $index; let pi = $index) {
                  <tr>
                    <td class="num">{{ fmt(p.x) }}</td>
                    <td class="num">{{ fmt(p.y) }}</td>
                    <td>
                      <input
                        class="cell-input num"
                        type="number"
                        step="0.01"
                        [value]="p.z"
                        (change)="setGridZ(pi, $any($event.target).value)"
                      />
                    </td>
                  </tr>
                }
              </tbody>
            }
          </table>
        </div>
      }
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="cancel()" i18n>Cancel</button>
  <button
    mat-flat-button
    color="primary"
    type="button"
    (click)="save()"
    [disabled]="!canSave"
  >
    @if (data.chart) {
      <span i18n>Save chart</span>
    } @else {
      <span i18n>Add chart</span>
    }
  </button>
</mat-dialog-actions>
